import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

/**
 * Integrações — a constelação de maquininhas e cartões em volta da Lukos.
 * Adaptado de Site Lukos (src/components/Integracoes.tsx): mesmo conceito de
 * anel com fios até o centro, portado para o design system deste projeto
 * (Tailwind com cores diretas, sem os tokens brand- e ink- do Site Lukos).
 */
const parceiros = [
  { arquivo: 'cielo.png', nome: 'Cielo' },
  { arquivo: 'rede.png', nome: 'Rede' },
  { arquivo: 'destaxa.png', nome: 'DeTaxa' },
  { arquivo: 'ticketlog.png', nome: 'Ticket Log' },
  { arquivo: 'semparar.png', nome: 'Sem Parar' },
  { arquivo: 'valecard.png', nome: 'ValeCard' },
  { arquivo: 'alelo.png', nome: 'Alelo' },
  { arquivo: 'xodo.png', nome: 'Xodó' },
];

// Oito posições do anel, em % do quadrado que envolve a constelação —
// todo parceiro à mesma distância do centro (Lukos), começando no topo.
const RAIO = 41;
const anel = Array.from({ length: 8 }, (_, i) => {
  const angulo = (-90 + i * 45) * (Math.PI / 180);
  return {
    esquerda: 50 + RAIO * Math.cos(angulo),
    topo: 50 + RAIO * Math.sin(angulo),
  };
});

export default function Integracoes() {
  return (
    <section className="relative overflow-hidden py-24 md:py-32">
      {/* Fundo geral (blobs + fios diagonais) vem do GlobalPageBackground,
          por trás de toda a página — aqui fica só o anel de círculos
          concêntricos, específico da constelação abaixo. */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden z-0">
        <svg
          aria-hidden
          className="absolute left-1/2 top-[-30%] h-[150%] w-[150%] -translate-x-1/2 text-purple-200/50"
          viewBox="0 0 800 800"
          fill="none"
        >
          {[180, 260, 340, 420].map((r) => (
            <circle key={r} cx="400" cy="400" r={r} stroke="currentColor" strokeWidth="1" strokeDasharray="2 10" />
          ))}
        </svg>
      </div>

      <div className="container relative z-10 mx-auto grid items-center gap-14 px-6 lg:grid-cols-[0.9fr_1.1fr]">
        {/* texto */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-[11px] font-black uppercase tracking-[0.3em] text-purple-600">
            Integrações
          </span>
          <h2 className="mt-4 text-3xl font-black leading-[1.05] tracking-tight text-slate-950 sm:text-5xl">
            A maquininha que você já usa{' '}
            <span className="font-black">cai conciliada</span>
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-slate-500">
            Cartão, vale e frota entram no sistema com a venda, e o valor que a
            operadora depositou é comparado com o que foi vendido. A diferença
            aparece na tela — não no fim do mês.
          </p>

          <ul className="mt-7 space-y-3">
            {[
              'Conciliação por adquirente, com taxa e prazo de repasse',
              'Cartão de frota tratado como o que é: venda a prazo',
              'O que não bateu fica separado, esperando decisão',
            ].map((t) => (
              <li key={t} className="flex items-start gap-2.5">
                <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-600">
                  <ArrowRight className="h-3 w-3" strokeWidth={3} />
                </span>
                <span className="text-[15px] leading-relaxed text-slate-700">
                  {t}
                </span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* constelação em anel */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1, duration: 0.8 }}
        >
          <div className="relative mx-auto aspect-square w-full max-w-[26rem]">
            {/* anel tracejado, com fade suave na borda via máscara radial */}
            <div
              className="absolute inset-[9%] rounded-full border-2 border-dashed border-slate-200"
              style={{
                WebkitMaskImage:
                  'radial-gradient(closest-side, black 88%, transparent 100%)',
                maskImage:
                  'radial-gradient(closest-side, black 88%, transparent 100%)',
              }}
            />
            <div className="pointer-events-none absolute inset-[22%] rounded-full bg-[radial-gradient(closest-side,rgba(124,58,237,0.14),transparent_75%)] blur-2xl" />

            {/* fios do miolo até cada selo */}
            <svg
              aria-hidden
              className="pointer-events-none absolute inset-0 h-full w-full text-purple-300"
              viewBox="0 0 100 100"
            >
              {anel.map((p) => (
                <line
                  key={`${p.esquerda}-${p.topo}`}
                  x1="50"
                  y1="50"
                  x2={p.esquerda}
                  y2={p.topo}
                  stroke="currentColor"
                  strokeWidth="0.35"
                  strokeDasharray="1.5 2"
                />
              ))}
            </svg>

            {/* Lukos no miolo */}
            <span className="absolute left-1/2 top-1/2 grid h-[34%] w-[34%] -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-gradient-to-br from-purple-700 to-slate-900 p-4 shadow-[0_24px_60px_-24px_rgba(107,31,176,0.95)]">
              <img
                src="/images/logo-lukos.png"
                alt="Lukos"
                className="h-auto w-[80%] object-contain brightness-0 invert"
              />
            </span>

            {/* parceiros ao redor */}
            {parceiros.map((parceiro, i) => (
              <span
                key={parceiro.arquivo}
                title={parceiro.nome}
                style={{
                  left: `${anel[i].esquerda}%`,
                  top: `${anel[i].topo}%`,
                }}
                className="absolute grid h-[21%] w-[21%] -translate-x-1/2 -translate-y-1/2 place-items-center overflow-hidden rounded-[1.15rem] bg-white ring-1 ring-slate-200 transition-transform duration-300 hover:scale-110"
              >
                <img
                  src={`/images/bandeiras/${parceiro.arquivo}`}
                  alt={parceiro.nome}
                  className="h-full w-full object-contain"
                />
              </span>
            ))}
          </div>

          <p className="mx-auto mt-6 max-w-sm text-center text-[11px] leading-relaxed text-slate-400">
            Marcas citadas indicam integração técnica; não implicam parceria
            comercial. Todas pertencem aos seus respectivos donos.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
